import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, UserRound, KeyRound, Eye, ShieldAlert, GraduationCap } from "lucide-react";
import { useStaffAuth } from "../context/StaffAuthContext";

import { updateStudentStatus, regenerateCredentials } from "../api/student/studentApi";
import { regenerateCredentialsPortal } from "../api/student/studentPortalApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  Button,
  Modal,
  Alert,
  EmptyState,
} from "../components/ui";

const STATUS_BADGE_VARIANTS = {
  ENROLLED: "success",
  LEFT: "danger",
  TRANSFERRED: "warning",
  GRADUATED: "info",
};

const STATUS_OPTIONS = ["ENROLLED", "LEFT", "TRANSFERRED", "GRADUATED"];

const StudentTable = ({ students = [], refreshStudents, isPortal = false }) => {
  const navigate = useNavigate();
  const { hasPermission } = useStaffAuth();

  const canManage = isPortal ? hasPermission("STUDENT_MANAGEMENT_FULL") : true;

  const [statusLoading, setStatusLoading] = useState(null);
  const [statusModal, setStatusModal] = useState(null);

  const [regenLoading, setRegenLoading] = useState(null);
  const [regenCredentials, setRegenCredentials] = useState(null);
  const [regenError, setRegenError] = useState("");

  const handleView = (id) => {
    navigate(isPortal ? `/staff-panel/students/${id}` : `/students/${id}`);
  };

  const handleStatusChange = async (status) => {
    try {
      setStatusLoading(statusModal.id);
      await updateStudentStatus(statusModal.id, status);
      setStatusModal(null);
      await refreshStudents();
    } catch (err) {
      console.error(err);
    } finally {
      setStatusLoading(null);
    }
  };

  const handleRegenerate = async (studentId) => {
    setRegenLoading(studentId);
    setRegenError("");
    try {
      const data = isPortal
        ? await regenerateCredentialsPortal(studentId)
        : await regenerateCredentials(studentId);

      setRegenCredentials(data?.credentials);
    } catch (err) {
      setRegenError(err?.response?.data?.message || "Failed to regenerate credentials");
      setTimeout(() => setRegenError(""), 3000);
    } finally {
      setRegenLoading(null);
    }
  };

  const columns = [
    {
      key: "student",
      header: "Student",
      render: (student) => (
        <div className="flex items-center gap-3 py-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">
            {student.gender === "Female" ? (
              <UserRound size={18} />
            ) : (
              <User size={18} />
            )}
          </div>
          <div>
            <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-xs sm:text-sm capitalize">
              {student.firstName} {student.middleName} {student.lastName}
            </p>
            <p className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500">
              {student.studentId}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "admissionNo",
      header: "Admission No",
      render: (student) => (
        <span className="font-medium text-xs text-neutral-800 dark:text-neutral-100 tracking-wide">
          {student.admissionNo}
        </span>
      ),
    },
    {
      key: "class",
      header: "Class",
      render: (student) => (
        <span className="text-xs text-neutral-700 dark:text-neutral-200 font-medium">
          Class {student.classId?.className || "-"}
        </span>
      ),
    },
    {
      key: "section",
      header: "Section",
      render: (student) => (
        <span className="text-xs text-neutral-700 dark:text-neutral-200 font-medium">
          Section {student.sectionId?.sectionName || "-"}
        </span>
      ),
    },
    {
      key: "contact",
      header: "Contact",
      nowrap: true,
      render: (student) => (
        <span className="font-medium text-xs text-neutral-800 dark:text-neutral-100 tracking-wide">
          {student.primaryContact}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (student) => (
        <Badge
          variant={STATUS_BADGE_VARIANTS[student.status] || "default"}
          className="text-[11px] font-semibold"
        >
          {student.status}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (student) => (
        <div className="flex items-center justify-end gap-1.5 py-1">
          {/* View Profile Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleView(student._id)}
            leftIcon={<Eye className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-300" />}
            title="View student profile details"
            className="text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            View Profile
          </Button>

          {canManage && (
            <>
              {/* Update Status Button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                isLoading={statusLoading === student._id}
                onClick={() =>
                  setStatusModal({ id: student._id, currentStatus: student.status })
                }
                leftIcon={<ShieldAlert className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-300" />}
                title="Change student status"
                className="text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                Update Status
              </Button>

              {/* Reset Password Button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                isLoading={regenLoading === student._id}
                onClick={() => handleRegenerate(student._id)}
                leftIcon={<KeyRound className="h-3.5 w-3.5 text-sky-600" />}
                title="Regenerate login credentials"
                className="text-xs font-medium text-sky-600 hover:bg-sky-50 hover:text-sky-700"
              >
                Reset Password
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {regenError && (
        <Alert variant="danger" title="Credential Error">
          {regenError}
        </Alert>
      )}

      <Card>
        <CardHeader className="border-b bg-neutral-50/50 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <GraduationCap className="h-5 w-5 text-neutral-700 dark:text-neutral-200 shrink-0" />
              <span>Student Roster</span>
            </CardTitle>
            <Badge variant="secondary" className="text-[11px]">
              Total: {students.length}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Table
            columns={columns}
            data={students}
            emptyState={
              <EmptyState
                icon={GraduationCap}
                title="No students found"
                description="Enrolled student records will be listed in this directory."
              />
            }
          />
        </CardContent>
      </Card>

      {/* Status Change Modal */}
      {statusModal && (
        <Modal
          isOpen={!!statusModal}
          onClose={() => setStatusModal(null)}
          title="Change Student Status"
          className="w-full max-w-sm"
        >
          <div className="space-y-4">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Current status:{" "}
              <Badge variant={STATUS_BADGE_VARIANTS[statusModal.currentStatus] || "default"}>
                {statusModal.currentStatus}
              </Badge>
            </p>

            <div className="grid grid-cols-1 gap-2">
              {STATUS_OPTIONS.map((status) => {
                const isCurrent = status === statusModal.currentStatus;
                return (
                  <Button
                    key={status}
                    type="button"
                    variant={isCurrent ? "secondary" : "outline"}
                    disabled={isCurrent || !!statusLoading}
                    onClick={() => handleStatusChange(status)}
                    className="w-full justify-start text-xs font-semibold"
                  >
                    {status}
                  </Button>
                );
              })}
            </div>

            <Button
              type="button"
              variant="ghost"
              onClick={() => setStatusModal(null)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </Modal>
      )}

      {/* Credentials Modal */}
      {regenCredentials && (
        <Modal
          isOpen={!!regenCredentials}
          onClose={() => setRegenCredentials(null)}
          title="Generated Student Credentials"
          className="w-full max-w-sm"
          footer={
            <Button
              type="button"
              variant="primary"
              onClick={() => setRegenCredentials(null)}
              className="w-full"
            >
              Done
            </Button>
          }
        >
          <div className="space-y-3">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
              Share these new login details securely with the student. The password will not be shown again.
            </p>

            <div className="rounded-card border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-500 dark:text-neutral-400">Username:</span>
                <span className="font-bold text-neutral-900 dark:text-neutral-100 tracking-wide">
                  {regenCredentials.username}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-500 dark:text-neutral-400">Password:</span>
                <span className="font-bold text-primary-600 dark:text-primary-400 tracking-wide">
                  {regenCredentials.password}
                </span>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StudentTable;